import { useEffect, useRef } from "react";
import { SURGE_COST, SURGE_DURATION_MS, SURGE_SPAWN_BONUS } from "../config/game";
import { useGameStore } from "../state/useGameStore";

/** One optional MASS spend after CORE - editorial, not a shop grid. */
export function BoostOverlay() {
  const boostOfferActive = useGameStore((s) => s.boostOfferActive);
  const score = useGameStore((s) => s.score);
  const acceptBoost = useGameStore((s) => s.acceptBoost);
  const dismissBoostOffer = useGameStore((s) => s.dismissBoostOffer);
  const acceptRef = useRef(null);
  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);

  useEffect(() => {
    if (!boostOfferActive) return undefined;
    restoreFocusRef.current = document.activeElement;
    const t = window.setTimeout(() => acceptRef.current?.focus(), 0);

    const onKeyDown = (e) => {
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll(
        'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      const list = Array.from(focusable);
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKeyDown);
      const prev = restoreFocusRef.current;
      if (prev && typeof prev.focus === "function") prev.focus();
    };
  }, [boostOfferActive]);

  if (!boostOfferActive) return null;

  const canAfford = score >= SURGE_COST;
  const seconds = Math.round(SURGE_DURATION_MS / 1000);

  return (
    <div
      className="climax climax--boost"
      role="dialog"
      aria-modal="true"
      aria-labelledby="boost-title"
      ref={panelRef}
    >
      <button
        type="button"
        className="climax__veil"
        aria-label="Decline surge"
        onClick={dismissBoostOffer}
      />
      <div className="climax__panel">
        <p className="climax__kicker">OPTIONAL</p>
        <h2 id="boost-title" className="climax__title climax__title--sm">
          SURGE
        </h2>
        <p className="climax__label">Spend mass for a brief swarm</p>
        <p className="climax__blurb">
          Cost {SURGE_COST} MASS. For {seconds}s, each strike and drip ejects +
          {SURGE_SPAWN_BONUS} extra fragment. Decline to keep forming as you are.
        </p>
        <div className="climax__actions">
          <button
            type="button"
            className="cta cta--signal"
            onClick={acceptBoost}
            ref={acceptRef}
            disabled={!canAfford}
          >
            <span className="cta__label">
              {canAfford ? `Spend ${SURGE_COST}` : "Need more mass"}
            </span>
          </button>
          <button
            type="button"
            className="cta cta--ghost"
            onClick={dismissBoostOffer}
          >
            <span className="cta__label">Not now</span>
          </button>
        </div>
        <p className="climax__hint">Enter · Esc</p>
      </div>
    </div>
  );
}
